import { createApi } from '@reduxjs/toolkit/dist/query/react';

import type { PermissionId } from '@app/api/team';
import { constructUrl } from '@app/api/utils';

import type { PermissionDetail } from '@app/features/project/pages/Contributors/models/permissionDetail';
import fetchBaseQuery from '../fetchBaseQuery';

export type ResendInvitePayload = {
    projectId: string;
    contributorIds: number[];
};

export type ResendInviteResponse = {
    id: string;
    success: boolean;
}[];

export type ImportFromProjectPayload = {
    projectId: string;
    fromProjectId: string;
    contributorIds: number[];
};

export type ImportFromProjectResponse = {
    id: string;
    success: boolean;
}[];

export type AddUserGroupsPayload = {
    projectId: string;
    userGroupIds: number[];
};

export type LanguageType = {
    id: string;
    label: string;
    isoCode: string;
    base: boolean;
};

export type ContributorType = {
    id: number;
    avatar: string;
    name: string;
    email: string;
    isPending: boolean;
    isReinvited: boolean;
    userGroups: {
        id: string;
        name: string;
        avatar: string;
    }[];
    allEditableLanguages: boolean;
    allReadOnlyLanguages: boolean;
    editableLanguages: {
        id: string;
        name: string;
        isoCode: string;
    }[];
    readOnlyLanguages: {
        id: string;
        name: string;
        isoCode: string;
    }[];
    permissions: {
        id: PermissionId;
        scope: string;
        title: string;
        group: string;
        checked: boolean;
        enabled: boolean;
    }[];
    roleId: number | null;
    owner: boolean;
    alphaProvider: boolean;
};

export type GetContributorsRequest = {
    projectId: string;
    q?: string;
    l?: string[];
    permissions?: string[];
};

export type EditContributorsRequest = {
    projectId: string;
    body: {
        id: number;
        allEditableLanguages?: boolean;
        allReadOnlyLanguages?: boolean;
        editableLanguages?: string[];
        readOnlyLanguages?: string[];
        permissions?: string[];
        roleId?: number | null;
    }[];
};

export type DeleteContributorsRequest = {
    projectId: string;
    body: number[];
};

export type DeleteUserGroupRequest = {
    projectId: string;
    groupId: string;
};

export type AddContributorsPayload = {
    projectId: string;
    emails: string[];
    roleId: number | null;
    allEditableLanguages: boolean;
    editableLanguages: number[];
    allReadOnlyLanguages: boolean;
    readOnlyLanguages: number[];
};

const contributorsApi = createApi({
    reducerPath: 'lokaliseInternal_contributors',
    baseQuery: fetchBaseQuery(),
    tagTypes: ['Contributor', 'userGroups', 'teamContributors'],
    endpoints: (builder) => ({
        getContributors: builder.query<ContributorType[], GetContributorsRequest>({
            query: ({ projectId, ...params }) => ({
                method: 'GET',
                url: constructUrl(`/contributors/beta/${projectId}/list`, { ...params }),
            }),
            providesTags: (contributors) =>
                contributors
                    ? [
                          ...contributors.map((contributor) => ({ type: 'Contributor' as const, id: contributor.id })),
                          { type: 'Contributor', id: 'LIST' },
                      ]
                    : [{ type: 'Contributor' as const, id: 'LIST' }],
        }),
        editContributors: builder.mutation<void, EditContributorsRequest>({
            query: ({ projectId, body }) => ({
                method: 'PATCH',
                url: `/contributors/beta/${projectId}/edit`,
                body: { contributors: body },
            }),
            invalidatesTags: (result, error, { body }) => [
                ...body.map((contributor) => ({ type: 'Contributor' as const, id: contributor.id })),
                { type: 'Contributor', id: 'LIST' },
            ],
        }),
        deleteContributors: builder.mutation<void, DeleteContributorsRequest>({
            query: ({ projectId, ...rest }) => ({
                method: 'DELETE',
                url: `/contributors/beta/${projectId}/delete`,
                body: rest.body,
            }),
            invalidatesTags: (result, error, { body }) => [
                ...body.map((contributorId) => ({ type: 'Contributor' as const, id: contributorId })),
                { type: 'Contributor', id: 'LIST' },
            ],
        }),
        getLanguages: builder.query<LanguageType[], { projectId: string }>({
            query: ({ projectId }) => ({
                method: 'GET',
                url: `/contributors/beta/${projectId}/languages`,
            }),
        }),
        getPermissions: builder.query<PermissionDetail[], { projectId?: string }>({
            query: ({ projectId }) => ({
                method: 'GET',
                url: projectId ? `/permissions?projectId=${projectId}` : `/permissions`,
            }),
        }),
        addUserGroups: builder.mutation<void, AddUserGroupsPayload>({
            query: (payload) => ({
                url: '/contributors/add-user-groups',
                method: 'POST',
                body: payload,
            }),
        }),
        resendInvite: builder.mutation<ResendInviteResponse, ResendInvitePayload>({
            query: ({ projectId, contributorIds }) => ({
                url: `/contributors/beta/${projectId}/reinvite`,
                method: 'POST',
                body: { contributorIds },
            }),
            invalidatesTags: (result, error, { contributorIds }) => [
                ...contributorIds.map((id) => ({ type: 'Contributor' as const, id })),
                { type: 'Contributor', id: 'LIST' },
            ],
        }),
        importFromProject: builder.mutation<ImportFromProjectResponse, ImportFromProjectPayload>({
            query: ({ projectId, fromProjectId, contributorIds }) => ({
                url: `/contributors/beta/${projectId}/import`,
                method: 'POST',
                body: { importFromProjectId: fromProjectId, contributorIds },
            }),
            invalidatesTags: (result, error, { contributorIds, projectId }) => [
                ...contributorIds.map((id) => ({ type: 'Contributor' as const, id })),
                { type: 'Contributor', id: 'LIST' },
            ],
        }),
        deleteUserGroup: builder.mutation<void, DeleteUserGroupRequest>({
            query: ({ projectId, groupId }) => ({
                method: 'DELETE',
                url: `/contributors/beta/${projectId}/user-groups/${groupId}`,
            }),
            invalidatesTags: (result, error, { groupId }) => (error ? [] : [{ type: 'userGroups', id: groupId }]),
        }),
        addContributors: builder.mutation<void, AddContributorsPayload>({
            query: ({ projectId, ...body }) => ({
                url: `/contributors/beta/${projectId}/add`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error) => (error ? [] : [{ type: 'Contributor', id: 'LIST' }]),
        }),
    }),
});

export default contributorsApi;

export const {
    useAddContributorsMutation,
    useDeleteUserGroupMutation,
    useGetContributorsQuery,
    useEditContributorsMutation,
    useDeleteContributorsMutation,
    useAddUserGroupsMutation,
    useGetLanguagesQuery,
    useGetPermissionsQuery,
    useResendInviteMutation,
    useImportFromProjectMutation,
} = contributorsApi;
